'use client';

/**
 * API namespace `admin.market.disputes` (sub-namespace extracted).
 * Verbatim extraction from frontend/src/lib/api-client.ts (originally within admin.market).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2 (relative to admin.market), T-EXPORT, T-IMPORTS.
 */

import { apiFetch, apiFetchFormData } from '../core/fetch-client';
import type {
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
} from '../core/types';
import type {
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  Paginated,
} from '../core/shared-types';

export const disputes = {
  /**
   * GET /api/admin/market/disputas — admin list (paginated). Returns
   * `AdminDisputeListResponse` (`disputes` summary array + `total`).
   * The optional `status` filter accepts any `DisputeStatus` value
   * (8 values: open / evidence_window / under_review / resolved_buyer
   * / resolved_seller / appealed / appeal_resolved / sla_expired).
   *
   * Authz: `requireSuperadminOrAdmin`.
   *
   * Spec ref: SPEC-084 Fase 6 §5.2 (GET /admin/market/disputas).
   */
  async list(
    filters?: AdminDisputeListFilters,
    signal?: AbortSignal,
  ): Promise<AdminDisputeListResponse> {
    const qs = new URLSearchParams();
    if (filters?.status) qs.set('status', filters.status);
    if (filters?.page !== undefined) qs.set('page', String(filters.page));
    if (filters?.limit !== undefined) qs.set('limit', String(filters.limit));
    const query = qs.toString();
    return apiFetch<AdminDisputeListResponse>(
      query ? `/admin/market/disputas?${query}` : '/admin/market/disputas',
      { signal },
    );
  },
  /**
   * GET /api/admin/market/disputas/:disputeId — admin detail (with
   * messages + evidence + order snapshot + buyer/seller user
   * snapshots). Returns `AdminDisputeDetailResponse`.
   *
   * Authz: `requireSuperadminOrAdmin`.
   *
   * Spec ref: SPEC-084 Fase 6 §5.2 (GET /admin/market/disputas/:disputeId).
   */
  async get(
    disputeId: string,
    signal?: AbortSignal,
  ): Promise<AdminDisputeDetailResponse> {
    return apiFetch<AdminDisputeDetailResponse>(
      `/admin/market/disputas/${encodeURIComponent(disputeId)}`,
      { signal },
    );
  },
  /**
   * POST /api/admin/market/disputas/:disputeId/resolve — admin
   * resolves the dispute (CAS status 'open'|'evidence_window'|
   * 'under_review' → 'resolved_buyer'|'resolved_seller'). Q5: regular
   * admin CAN resolve (only `appeal/resolve` is superadmin-only).
   *
   * Backend flow (Task B — `dispute.service.resolveDispute`):
   *   - CAS Dispute.status + set resolution sub-doc.
   *   - If outcome='favor_buyer' → refund buyer (escrowStatus →
   *     'refunded', Ledger debit escrow + credit buyer:credits).
   *   - If outcome='favor_seller' → release to seller (escrowStatus →
   *     'released', 30/2/68 split — Ledger 4 entries).
   *   - If outcome='split' → refund buyer the `refundCredits` amount
   *     (escrowStatus → 'refunded'). Q1 PENDIENTE-FOUNDER: 'split'
   *     behavior is ambiguous (does the seller receive the remainder?).
   *   - If `fraudulent=true` → applyStrike per INV-MKT-17 (Strike
   *     1=warning, Strike 2=7d suspension, Strike 3=permanent ban).
   *   - Set `appealWindowExpiresAt = now + 3d` (INV-MKT-16).
   *
   * Authz: `requireSuperadminOrAdmin` + `requireRecent2FA` (financial-
   * grade — refund OR release). On 403 `2FA_REQUIRED`, the global
   * admin 2FA dialog opens automatically.
   *
   * Spec ref: SPEC-084 Fase 6 §5.2 (POST /resolve); §3.1
   *           (resolutionSchema); §4.1 step 4 (resolveDispute CAS +
   *           refund/release + applyStrike); SPEC-NEGOCIO §10.9
   *           (INV-MKT-16 SLA + INV-MKT-17 strikes).
   */
  async resolve(
    disputeId: string,
    body: AdminResolveDisputeBody,
    signal?: AbortSignal,
  ): Promise<DisputeResponse> {
    return apiFetch<DisputeResponse>(
      `/admin/market/disputas/${encodeURIComponent(disputeId)}/resolve`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        signal,
      },
    );
  },
  /**
   * POST /api/admin/market/disputas/:disputeId/appeal/resolve —
   * superadmin resolves the appeal (Q5: SOLO superadmin — admin
   * regular NO puede). CAS status 'appealed' → 'appeal_resolved'.
   *
   * Backend flow (Task B — `dispute.service.resolveAppeal`):
   *   - Q5 check: `req.user.role === 'superadmin'` (middleware-level).
   *   - CAS Dispute.status + set appeal.outcome + resolvedBy + resolvedAt.
   *   - If outcome='upheld' → original resolution stands (no Ledger
   *     changes — the original refund/release already happened).
   *   - If outcome='overturned' → reversal (re-execute the OPPOSITE
   *     refund/release: if original was favor_seller → now refund
   *     buyer; if favor_buyer → now release to seller). Ledger
   *     entries of reversal.
   *
   * Authz: `requireSuperadmin` + `requireRecent2FA`. On 403
   * `AUTH_FORBIDDEN` (admin regular calling superadmin-only endpoint),
   * the caller's catch block surfaces the error via toast (NO 2FA
   * dialog — this is an authorization failure, not an auth failure).
   *
   * Spec ref: SPEC-084 Fase 6 §5.2 (POST /appeal/resolve); §3.1
   *           (appealSchema); §4.1 step 6 (resolveAppeal — Q5
   *           superadmin check + overturned reversión); SPEC-NEGOCIO
   *           §10.9 ("El superadmin tiene autoridad final" — INV-MKT-16).
   */
  async resolveAppeal(
    disputeId: string,
    body: AdminResolveAppealBody,
    signal?: AbortSignal,
  ): Promise<DisputeResponse> {
    return apiFetch<DisputeResponse>(
      `/admin/market/disputas/${encodeURIComponent(disputeId)}/appeal/resolve`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        signal,
      },
    );
  },
};
